import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Popper,
  Paper,
  useMediaQuery,
  useTheme,
  Typography,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro';
import { BsCalendar4Range } from 'react-icons/bs';
import moment from 'moment';
import { AppVarsContext } from '../../../context/AppVarsContext';
import {
  getExecutiveGlassCardSx,
  getExecutiveGlassIconButtonSx,
} from '../../../views/shared/reportSurfaceStyles';

export const buildDatePresetGroups = (now = moment()) => {
  const today = now.clone().startOf('day');
  const current = (unit) => [today.clone().startOf(unit), today.clone()];
  const previous = (unit) => [today.clone().subtract(1, unit).startOf(unit), today.clone().subtract(1, unit).endOf(unit)];
  const recent = (days) => [today.clone().subtract(days - 1, 'days'), today.clone()];
  const weekStart = today.clone().day(0);
  return [
    { title: 'Recent days', presets: [
      { label: 'Today', range: recent(1) },
      { label: 'Yesterday', range: [today.clone().subtract(1, 'day'), today.clone().subtract(1, 'day')] },
      { label: 'Last 7 days', range: recent(7) },
      { label: 'Last 14 days', range: recent(14) },
      { label: 'Last 28 days', range: recent(28) },
    ] },
    { title: 'Weeks', presets: [
      { label: 'This week', range: [weekStart.clone(), today.clone()] },
      { label: 'Last week', range: [weekStart.clone().subtract(7, 'days'), weekStart.clone().subtract(1, 'day')] },
    ] },
    { title: 'Months', presets: [{ label: 'This month', range: current('month') }, { label: 'Last month', range: previous('month') }] },
    { title: 'Quarters', presets: [{ label: 'This quarter', range: current('quarter') }, { label: 'Last quarter', range: previous('quarter') }] },
    { title: 'Years', presets: [{ label: 'This year', range: current('year') }, { label: 'Last year', range: previous('year') }] },
  ];
};

const normalizeStoredMinDate = (value) => {
  if (!value) return '';
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const clampRangeToMinDate = (nextRange, minDate) => {
  const defaultStart = minDate ? minDate.clone() : moment().subtract(7, 'days');
  const rawStart = nextRange?.[0] ? moment(nextRange[0]) : defaultStart.clone();
  const start = rawStart.isValid() ? rawStart : defaultStart.clone();
  const clampedStart = minDate && start.isBefore(minDate, 'day') ? minDate.clone() : start.clone();

  const rawEnd = nextRange?.[1] ? moment(nextRange[1]) : clampedStart.clone();
  const end = rawEnd.isValid() ? rawEnd : clampedStart.clone();
  const clampedEnd = end.isBefore(clampedStart, 'day') ? clampedStart.clone() : end.clone();

  return [clampedStart, clampedEnd];
};

export const syncSessionRange = (nextStart, nextEnd) => {
    const nextStartValue = nextStart.format('YYYY-MM-DD');
    const nextEndValue = nextEnd.format('YYYY-MM-DD');
    const previousStartValue = sessionStorage.getItem('sDate');
    const previousEndValue = sessionStorage.getItem('eDate');

    sessionStorage.setItem('sDate', nextStartValue);
    sessionStorage.setItem('eDate', nextEndValue);

    if (previousStartValue !== nextStartValue || previousEndValue !== nextEndValue) {
      window.dispatchEvent(new CustomEvent('dc', { detail: { sDate: nextStartValue, eDate: nextEndValue } }));
    }
  };


const DateRangeSelector = ({ sDate, eDate, icon = true, childToParent }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { appVars } = useContext(AppVarsContext);
  const [hasSelectedFirstDate, setHasSelectedFirstDate] = useState(false);
  const [minSelectableDate, setMinSelectableDate] = useState('');
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const childToParentRef = useRef(childToParent);

  const apiUrl = appVars?.api_url;
  const profileId = sessionStorage.getItem('rid');
  const minSelectableMoment = useMemo(
    () => (minSelectableDate ? moment(minSelectableDate, 'YYYY-MM-DD', true) : null),
    [minSelectableDate]
  );

  const ensureSessionDates = () => {
    const storedStart = sessionStorage.getItem('sDate');
    const storedEnd = sessionStorage.getItem('eDate');

    if (!storedStart || !storedEnd) {
      const fallbackStart = (minSelectableMoment ? minSelectableMoment.clone() : moment().subtract(7, 'days')).format('YYYY-MM-DD');
      const fallbackEnd = moment().format('YYYY-MM-DD');
      sessionStorage.setItem('sDate', fallbackStart);
      sessionStorage.setItem('eDate', fallbackEnd);
      return { sDate: fallbackStart, eDate: fallbackEnd };
    }

    return { sDate: storedStart, eDate: storedEnd };
  };

  const normalizeDate = (dateStr, fallback) => {
    const parsed = dateStr ? moment(dateStr) : moment(fallback);
    return parsed.isValid() ? parsed : moment(fallback);
  };

  const sessionDefaults = useMemo(() => ensureSessionDates(), []);

  const [value, setValue] = useState([
    ...clampRangeToMinDate(
      [
        normalizeDate(sessionStorage.getItem('sDate') || sDate || sessionDefaults.sDate, minSelectableMoment || moment().subtract(7, 'days')),
        normalizeDate(sessionStorage.getItem('eDate') || eDate || sessionDefaults.eDate, moment()),
      ],
      minSelectableMoment
    ),
  ]);


  useEffect(() => {
    childToParentRef.current = childToParent;
  }, [childToParent]);

  useEffect(() => {
    const update = () => {
      setValue(clampRangeToMinDate([
        moment(sessionStorage.getItem('sDate')),
        moment(sessionStorage.getItem('eDate')),
      ], minSelectableMoment));
      setHasSelectedFirstDate(false);
    };
    window.addEventListener('dc', update);
    return () => window.removeEventListener('dc', update);
  }, [minSelectableMoment]);


  useEffect(() => {
    let active = true;

    if (!apiUrl || !profileId) {
      setMinSelectableDate('');
      return undefined;
    }

    const fetchProfile = async () => {
      try {
        const response = await fetch(`${apiUrl}/reportprofile/${profileId}`, {
          method: 'GET',
          redirect: 'follow',
        });

        if (!response.ok) {
          throw new Error(`Profile lookup failed (${response.status})`);
        }

        const profile = await response.json();
        const settings = typeof profile?.settings === 'object'
          ? profile.settings
          : (profile?.settings ? JSON.parse(profile.settings) : {});
        const nextMinDate = normalizeStoredMinDate(settings?.date_range_min);

        if (active) {
          setMinSelectableDate(nextMinDate);
        }
      } catch (error) {
        if (active) {
          setMinSelectableDate('');
        }
      }
    };

    fetchProfile();

    return () => {
      active = false;
    };
  }, [apiUrl, profileId]);

  useEffect(() => {
    const [nextStart, nextEnd] = clampRangeToMinDate(
      [
        normalizeDate(sessionStorage.getItem('sDate') || sDate || sessionDefaults.sDate, minSelectableMoment || moment().subtract(7, 'days')),
        normalizeDate(sessionStorage.getItem('eDate') || eDate || sessionDefaults.eDate, moment()),
      ],
      minSelectableMoment
    );

    setValue((currentValue) => {
      if (
        currentValue?.[0]?.isSame(nextStart, 'day') &&
        currentValue?.[1]?.isSame(nextEnd, 'day')
      ) {
        return currentValue;
      }

      return [nextStart, nextEnd];
    });
  }, [sDate, eDate, sessionDefaults.sDate, sessionDefaults.eDate, minSelectableMoment]);

  useEffect(() => {
    const storedStart = normalizeDate(sessionStorage.getItem('sDate') || sDate || sessionDefaults.sDate, minSelectableMoment || moment().subtract(7, 'days'));
    const storedEnd = normalizeDate(sessionStorage.getItem('eDate') || eDate || sessionDefaults.eDate, moment());
    const [nextStart, nextEnd] = clampRangeToMinDate([storedStart, storedEnd], minSelectableMoment);

    const shouldSync =
      !storedStart.isValid() ||
      !storedEnd.isValid() ||
      !storedStart.isSame(nextStart, 'day') ||
      !storedEnd.isSame(nextEnd, 'day');

    if (!shouldSync) return;

    syncSessionRange(nextStart, nextEnd);
  }, [eDate, minSelectableMoment, sDate, sessionDefaults.sDate, sessionDefaults.eDate]);

  const presetGroups = buildDatePresetGroups();

  const applySelectedRange = (nextRange) => {
    const normalizedRange = clampRangeToMinDate(nextRange, minSelectableMoment);
    const [nextStart, nextEnd] = normalizedRange;

    setValue(normalizedRange);
    setHasSelectedFirstDate(false);
    setOpen(false);
    syncSessionRange(nextStart, nextEnd);
    childToParentRef.current?.({
      sDate: nextStart.format('YYYY-MM-DD'), eDate: nextEnd.format('YYYY-MM-DD'),
      startDate: nextStart.toDate(), endDate: nextEnd.toDate(),
    });
  };

  const appliedStart = value?.[0] || normalizeDate(sessionStorage.getItem('sDate') || sDate || sessionDefaults.sDate, moment());
  const appliedEnd = value?.[1] || normalizeDate(sessionStorage.getItem('eDate') || eDate || sessionDefaults.eDate, moment());
  const iconButtonSx = getExecutiveGlassIconButtonSx(theme);
  const triggerPillSx = {
    px: 1.15,
    py: 0.7,
    borderRadius: 999,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#ffffff', 0.68)} 100%)`,
    boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.07)}`,
    minWidth: 0,
  };
  const calendarSx = {
    px: { xs: 0.4, sm: 0.75 },
    py: 0.75,
    '& .MuiPickersCalendarHeader-root': {
      px: 1.2,
      pt: 0.4,
      color: alpha(theme.palette.text.primary, 0.84),
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: alpha(theme.palette.text.primary, 0.58),
      fontWeight: 700,
      letterSpacing: '0.04em',
    },
    '& .MuiPickersDay-root': {
      borderRadius: 1.6,
      fontWeight: 600,
      color: alpha(theme.palette.text.primary, 0.84),
    },
    '& .MuiPickersDay-root.Mui-selected, & .MuiDateRangePickerDay-day.Mui-selected': {
      backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      color: '#fff',
    },
    '& .MuiDateRangePickerDay-rangeIntervalDayHighlight': {
      background: alpha(theme.palette.primary.main, 0.11),
    },
    '& .MuiDateRangePickerDay-rangeIntervalPreview': {
      borderColor: alpha(theme.palette.primary.main, 0.18),
      borderRadius: 1.6,
    },
    '& .MuiPickersArrowSwitcher-button': {
      ...iconButtonSx,
      width: 34,
      height: 34,
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center', gap: { xs: 0.55, md: 0.8 }
        }}>
          <Box sx={{ gap: 0.75, display: { xs: 'none', md: 'flex' } }}>
            <Box sx={triggerPillSx}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'text.primary' }}>
                {appliedStart.format('MM-DD-YYYY')}
              </Typography>
            </Box>
            <Box sx={triggerPillSx}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'text.primary' }}>
                {appliedEnd.format('MM-DD-YYYY')}
              </Typography>
            </Box>
          </Box>

          {icon && (
            <IconButton
              aria-label={`Choose date range, ${appliedStart.format('MM-DD-YYYY')} to ${appliedEnd.format('MM-DD-YYYY')}`}
              sx={iconButtonSx}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setOpen((prev) => !prev);
                setHasSelectedFirstDate(false);
              }}
            >
              <BsCalendar4Range color={theme.palette.primary.main} size={20} />
            </IconButton>
          )}
        </Box>

        <Popper
          sx={{ zIndex: 1800 }}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        >
          <Paper
            sx={{
              ...getExecutiveGlassCardSx(theme),
              mt: 0.8,
              width: isMobile ? 'calc(100vw - 24px)' : 'auto',
              maxWidth: '100%',
              overflow: 'auto',
              maxHeight: 'calc(100dvh - 96px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 0,
              }}
            >
              <DateRangeCalendar
                calendars={isMobile ? 1 : 2}
                minDate={minSelectableMoment || undefined}
                sx={calendarSx}
                value={value}
                onChange={(newValue) => {
                  if (!hasSelectedFirstDate) {
                    const firstDate = newValue[0] || newValue[1];
                    if (firstDate) {
                      const [clampedFirstDate] = clampRangeToMinDate([firstDate, firstDate], minSelectableMoment);
                      setValue([clampedFirstDate, clampedFirstDate]);
                      setHasSelectedFirstDate(true);
                    }
                    return;
                  }

                  const firstDate = value[0];
                  const [start, end] = newValue;
                  let secondDate = null;

                  if (start && !start.isSame(firstDate, 'day')) {
                    secondDate = start;
                  }
                  if (!secondDate && end && !end.isSame(firstDate, 'day')) {
                    secondDate = end;
                  }

                  if (!secondDate) {
                    applySelectedRange([firstDate, firstDate]);
                    return;
                  }

                  const ordered = [firstDate, secondDate].sort(
                    (a, b) => a.valueOf() - b.valueOf()
                  );

                  applySelectedRange(ordered);
                }}
              />
              <Stack
                spacing={1}
                sx={{
                  width: isMobile ? 'auto' : 272,
                  flexShrink: 0,
                  background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                  p: 1.35,
                  borderLeft: { md: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` },
                  borderTop: { xs: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, md: 'none' },
                }}
              >
                <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: '0.12em', color: 'text.secondary' }}>
                  Quick Ranges
                </Typography>
                {minSelectableDate ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.92 }}>
                    Earliest date: {moment(minSelectableDate, 'YYYY-MM-DD', true).format('MM-DD-YYYY')}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">Current periods end today. Weeks start Sunday.</Typography>
                {presetGroups.map(group => <Box key={group.title} role="group" aria-label={group.title}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>{group.title}</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                    {group.presets.map(({ label, range }) => {
                      const disabled = Boolean(minSelectableMoment && range[1].isBefore(minSelectableMoment, 'day'));
                      const [start, end] = clampRangeToMinDate(range, minSelectableMoment);
                      const selected = !hasSelectedFirstDate && !disabled && start.isSame(appliedStart, 'day') && end.isSame(appliedEnd, 'day');
                      return <Tooltip key={label} title={disabled ? 'This period is before the earliest available date.' : `${start.format('MMM D, YYYY')} to ${end.format('MMM D, YYYY')}`}>
                        <span><Button
                          fullWidth disabled={disabled} aria-pressed={selected}
                          variant={selected ? 'outlined' : 'text'} size="small"
                          onClick={() => applySelectedRange(range)}
                          sx={{ minHeight: 44, justifyContent: 'flex-start', px: 1, borderRadius: 2, color: selected ? 'primary.main' : 'text.primary', fontWeight: selected ? 700 : 500, textTransform: 'none', bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : undefined }}
                        >{label}</Button></span>
                      </Tooltip>;
                    })}
                  </Box>
                </Box>)}
              </Stack>
            </Box>
          </Paper>
        </Popper>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;
